import { ApiMasterDataAdvancedQuery } from '../../../../common/api/api-interfaces';
import { DataTypes } from '../../../../common/constants/constants';
import { TimeSliderConfig } from '../../../../common/filter/filter-store';
import { DataMapAndLabels, Service } from '../../../../common/services/utils';
import { HasDependencies, VersionId } from '../../../../common/v2/api/types';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../../../common/v2/context/legacy-types';
import {
  ApiMasterDataQueryFilterItem,
  DataFieldWithDataType,
} from '../../../../common/v2/types';
import { Cycle } from '../survey-store';
import { SurveyMetrics } from './../survey-store';

export enum Sentiments {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NEUTRAL = 'NEUTRAL',
  NA = 'NA',
}

export interface OverallQuestionTotalServiceInputs {
  timeSliderConfig: TimeSliderConfig;
  selectedOverallQuestion: string;
  selectedCycles: Cycle[];
  selectedMetricValue: SurveyMetrics;
  temporalityFilter: ApiMasterDataQueryFilterItem;
}

export interface OverallQuestionByDimensionServiceInputs {
  timeSliderConfig: TimeSliderConfig;
  dimension: DataFieldWithDataType;
  selectedOverallQuestion: string;
  selectedCycles: Cycle[];
  metricsRadioValue: SurveyMetrics;
  temporalityFilter: ApiMasterDataQueryFilterItem;
}

export interface QuestionServiceInputs {
  timeSliderConfig: TimeSliderConfig;
  dimension: DataFieldWithDataType;
  selectedQuestions: string[];
  selectedCycles: Cycle[];
  selectedTheme: string;
  myPopFilter: ApiMasterDataQueryFilterItem;
  locationFilter: ApiMasterDataQueryFilterItem;
  organizationFilter: ApiMasterDataQueryFilterItem;
}

export interface QuestionAvgScoreByPopulationInputs {
  selectedMetricValue: SurveyMetrics;
  selectedCycles: Cycle[];
  selectedTheme: string;
  timeSliderConfig: TimeSliderConfig;
  myPopFilters: ApiMasterDataQueryFilterItem[];
  locationFilters: ApiMasterDataQueryFilterItem[];
  organizationFilters: ApiMasterDataQueryFilterItem[];
}

export interface QuestionAvgScoreInputs {
  selectedMetricValue: SurveyMetrics;
  selectedCycles: Cycle[];
  temporalityFilter: ApiMasterDataQueryFilterItem;
  timeSliderConfig: TimeSliderConfig;
}

export interface AllQuestionsWithSentimentInputs {
  selectedCycle: Cycle;
  temporalityFilter: ApiMasterDataQueryFilterItem;
}

export interface SurveySentimentResponse {
  [question: string]: SurveySentimentMap;
}

export interface SurveyQuestionResult {
  [cycleLabel: string]: {
    [theme: string]: {
      [question: string]: number;
    };
  };
}
export interface SurveyQuestionResponse {
  response: SurveyQuestionResult;
  selectedCycles: Cycle[];
  selectedMetricValue: SurveyMetrics;
}

export interface QuestionAvgScoreByPopulationResponse {
  company: Record<string, Record<string, number>>;
  myFilteredPop: Record<string, Record<string, number>>;
  myPop: Record<string, Record<string, number>>;
  location: Record<string, Record<string, number>>;
  organization: Record<string, Record<string, number>>;
}

export type SurveySentimentMap = Record<
  Sentiments,
  {
    count: number;
    percentage: number;
  }
>;

export interface SurveyService extends HasDependencies {
  questionsReportQuery: (
    selectedCycles: Cycle[],
    fields: DataFieldWithDataType[],
    filters: ApiMasterDataQueryFilterItem[],
    selectedQuestions: string[],
    latestEmployeeVersion: string,
    timeSliderConfig: TimeSliderConfig
  ) => ApiMasterDataAdvancedQuery;

  overallQuestionTotal: Service<
    OverallQuestionTotalServiceInputs,
    DataMapAndLabels
  >;
  overallQuestionByDimensionAvgScores: Service<
    OverallQuestionByDimensionServiceInputs,
    DataMapAndLabels
  >;
  overallQuestionByDimensionRespondents: Service<
    OverallQuestionByDimensionServiceInputs,
    DataMapAndLabels
  >;

  getAllQuestionsWithSentiment: Service<
    AllQuestionsWithSentimentInputs,
    SurveySentimentResponse
  >;
  questionByDimension: Service<QuestionServiceInputs, DataMapAndLabels>;

  overallQuestionAvgScore(
    inputs: OverallQuestionByDimensionServiceInputs,
    filters: ApiMasterDataQueryFilterItem[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ): Promise<{
    dataMap: Record<string, any>;
    labels: string[];
  }>;

  questionAvgScore: Service<QuestionAvgScoreInputs, SurveyQuestionResponse>;

  getAllCycles(
    questionTypes?: QuestionTypes[],
    answerTypes?: AnswerTypes[]
  ): Promise<Cycle[]>;

  getAllQuestions(
    cycles: Cycle[],
    questionTypes: QuestionTypes[],
    answerTypes: AnswerTypes[]
  ): Promise<string[]>;

  getAllScores(): Promise<string[]>;
}

export enum QuestionTypes {
  OVERALL = 'OVERALL',
  SPECIFIC = 'SPECIFIC',
}

export enum AnswerTypes {
  NOMINAL = 'NOMINAL',
  ORDINAL = 'ORDINAL',
}
